import { useEffect, useContext, useRef } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HighchartsMap from 'highcharts/modules/map';
import { AppContext } from '../../../../../../Context/AppContext';
import { MapContainer, Spacer, GeoChartContainer } from './PowerNcfVolumeMap.style';
import mapdatanew from '@highcharts/map-collection/custom/world.topo.json';
import { ncfByCountry } from '../../../../../../api/services';
import isEqual from 'lodash/isEqual';
import { errorNotifyMessage, isApiResponseEmpty } from '../../../../../Utils/utils';
import { notify } from '../../../../../../../CarbonDashboard/components/Toast/notify';

// Initialize the map module
if (typeof Highcharts === 'object') {
  HighchartsMap(Highcharts);
}

interface PowerNCFVolumeMapProps {
  setIsNcfByCountryResponseEmpty: (isEmpty: boolean) => void;
}

const PowerNCFVolumeMap = ({ setIsNcfByCountryResponseEmpty }: PowerNCFVolumeMapProps) => {
  const { state: analyticsState, dispatch: analyticsDispatch } =
    useContext(AppContext).analyticsHome;

  const { state: appDataState } = useContext(AppContext).appData;
  const isRegionSpecificUser = Object.values(appDataState.userRegionFlags).some((el) => el);
  const prevFilteredValuesRef = useRef(analyticsState.filteredValues);

  useEffect(() => {
    // This triggers for global users
    if (!isEqual(prevFilteredValuesRef.current, analyticsState.filteredValues)) {
      fetchData();
      // Update the refs with the current values
      prevFilteredValuesRef.current = analyticsState.filteredValues;
    }
  }, [analyticsDispatch, analyticsState.filteredValues]);

  useEffect(() => {
    // This triggers for a region specific users. Ex. Asia or Australia
    if (!isRegionSpecificUser) {
      fetchData();
    }
  }, []);

  const fetchData = async () => {
    try {
      const response = await ncfByCountry(analyticsState.filteredValues);
      analyticsDispatch({
        type: 'setNcfByCountry',
        payload: response.data,
      });
      setIsNcfByCountryResponseEmpty(isApiResponseEmpty(
        response.data,
        analyticsState.filteredValues.region,
        analyticsState.filteredValues.commodity,
      ));

    } catch (error) {
       notify('error', errorNotifyMessage(error));
    }
  };

  const getOnClicked = () => {
    // Handle on bubble click here
  };

  const mapData = analyticsState.ncfByCountry.map(
    (item: { COUNTRY_CODE: string; QTY: number; UNIT: string }) => ({
      code3: item?.COUNTRY_CODE,
      z: item?.QTY,
      UNIT: item?.UNIT,
    }),
  );

  const chartOptions: Highcharts.Options = {
    chart: {
      borderWidth: 0,
      map: mapdatanew as any,
    },
    mapNavigation: {
      enabled: true,
      buttonOptions: {
        verticalAlign: 'bottom',
      },
    },
    title: {
      text: 'NCF Volume Map',
      align: 'left',
    },
    legend: {
      enabled: false,
    },
    credits: {
      enabled: false,
    },
    plotOptions: {
      series: {
        point: {
          events: {
            click: () => getOnClicked(),
          },
        },
      },
    },
    series: [
      {
        name: 'Countries',
        color: '#E0E0E0',
        borderColor: '#A0A0A0',
        nullColor: 'rgba(200, 200, 200, 0.3)',
        showInLegend: false,
        enableMouseTracking: false,
      } as any,
      {
        type: 'mapbubble',
        name: 'Countries',
        joinBy: ['iso-a3', 'code3'],
        data: mapData,
        minSize: 10,
        maxSize: '8%',
        tooltip: {
          pointFormat:
            '<span style="font-weight: 600;">Country Name:</span> {point.name}<br><span style="font-weight: 600;">NCF Volume:</span> {point.z}<br><span style="font-weight: 600;">UNIT:</span> {point.UNIT}',
          headerFormat: '',
        },
      } as any,
    ],
  };

  return (
    <MapContainer data-testid='map-container'>
      <Spacer>
        <GeoChartContainer data-testid='geo-chart'>
          <HighchartsReact
            highcharts={Highcharts}
            constructorType={'mapChart'}
            options={chartOptions}
          />
          <div
            style={{ display: 'none' }}
            data-testid='analytics-ncf-volume-map-hidden-for-automation'
          >
            {JSON.stringify(mapData)}
          </div>
        </GeoChartContainer>
      </Spacer>
    </MapContainer>
  );
};

export default PowerNCFVolumeMap;